import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import DropdownButton from "react-bootstrap/DropdownButton"
import Dropdown from "react-bootstrap/Dropdown"
import PlanningModeButtons from "./PlanningModeButtons"
import "./index.scss"

import Button from "react-bootstrap/Button"
import { Popup, UpdateIndicator, SimulationLogoutConfirm } from "components"
import { MemberConfig } from "components/Config"
import DateButtons from "./DateButtons"

import { visual, teamStatus, auth } from "state_management"
import { DateTime } from "luxon"

import { FiSettings, FiMenu, FiHome, FiLogOut } from "react-icons/fi"
import { RiUserSettingsLine } from "react-icons/ri"
import { TbArrowsMaximize, TbArrowsMinimize } from "react-icons/tb";
import isOnMobile from "utils/scripts/isOnMobile"
import useT from "utils/hooks/useT"


export const setMondayDate = (date, mondayDate, dispatch) => {
  const thisMonday = date.startOf("week")
  if (!mondayDate.hasSame(thisMonday, "day")) {
    dispatch(visual.changeMondayDate(thisMonday.toISO()))
  }
}

// Helper function to map screen types to screen keys
const getScreenKey = (type) => {
  switch (type) {
    case "team-plan":
      return "teamPlan"
    case "asset-view":
      return "assetView"
    case "single-member":
      return "singleMember"
    default:
      return null
  }
}
// Buttons used on various schedule screens.
export default function ScheduleButtons({ type, member, moveLeft, moveRight, view }) {
  const dispatch = useDispatch()
  const { t } = useT("common")
  const location = useLocation()
  const navigate = useNavigate()
  const mondayDate = DateTime.fromISO(useSelector((state) => state.visual.scheduleMondayDate))
  const activeDate = DateTime.fromISO(useSelector((state) => state.visual.activeDate))
  const showWeekendAssetView = useSelector((state) => state.visual.assetView.showWeekend)
  const isInPlanningMode = useSelector((state) => state.teamStatus.isInPlanningMode)
  const isCurrentPlanner = useSelector((state) => state.teamStatus.isCurrentPlanner)
  const isMember = useSelector((state) => state.auth.isMember)
  const memberId = useSelector((state) => state.auth.memberId)
  const showProjects = useSelector((state) => state.teamStatus.showProjects)
  const showAnalyses = useSelector((state) => state.teamStatus.showAnalyses)
  const showLogoutConfirm = useSelector((state) => state.teamStatus.showLogoutConfirm)
  const directToMemberPlan = useSelector((state) => state.teamStatus.directToMemberPlan)
  const showLibrarian = useSelector((state) => state.teamStatus.showLibrarian)
  const isPlanner = useSelector((state) => state.auth.role) === "planner"
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)
  const currentMemberShowOnPlan = useSelector((state) => memberId && state.members[memberId] ? state.members[memberId].showOnPlan : true)

  const [activeConfig, setActiveConfig] = useState(null)
  
  // Get screen key for this component
  const screenKey = getScreenKey(type)
  
  // Use screen-specific settings for supported screens
  const withWeekend = useSelector((state) => 
    screenKey ? state.visual.screens[screenKey].scheduleWithWeekend : state.visual.scheduleWithWeekend
  )
  const withWeek = useSelector((state) => 
    screenKey ? state.visual.screens[screenKey].scheduleWithWeek : state.visual.scheduleWithWeek
  )
  const showCompact = useSelector((state) => 
    screenKey ? state.visual.screens[screenKey].showCompact : state.visual.showCompact
  )


  const isDayView = view === "day-view"
  let dropDownOptions = []
  dropDownOptions.push({ url: "/", name: t("teamPlan") })
  dropDownOptions.push({ url: "/asset-view", name: t("instrumentView") })
  if (showAnalyses) {
    dropDownOptions.push({ url: "/sample-view", name: t("sampleView") })
  }
  if (showProjects) {
    dropDownOptions.push({ url: "/project-view", name: t("projectView") })
  }
  //if (showProjects) {
  //  dropDownOptions.push({ url: "/scrum-view", name: "Scrum View" })
  //}
  if (currentMemberShowOnPlan) {
    dropDownOptions.push({ url: `/member/${memberId}`, name: t("memberPlan") })
  }
  if (isPlanner) {
    dropDownOptions.push({ url: "/setup/members", name: t("settings") })
  }
  const isCreatingProject = location.pathname === "/setup/projects/create"
  const isEditingProject = location.pathname !== "/setup/projects/create" && location.pathname.includes("/setup/projects/")

  const isStandalone = () => {
    return (window.matchMedia('(display-mode: standalone)').matches)
  }

  const handleLibrarianNavigation = () => {
    if (isStandalone()) {
      navigate('/librarian')
    } else {
      window.open('/librarian', '_blank')
    }
  }



  return (
    <>
      {showLogoutConfirm && <SimulationLogoutConfirm/>}

      {activeConfig && (
        <Popup nonScrollable onCancel={() => setActiveConfig(false)}>
          <MemberConfig member={member} onClose={() => setActiveConfig(false)} />
        </Popup>
      )}
      <div className={"ScheduleButtons" + (moveLeft ? " move-left" : "") + (moveRight ? " move-right" : "")}>
        <div className="leftmenu-button">
          {type === "team-plan" && !isMember && !isViewOnly && (
            <Button title={t("openMetricsPanel")} onClick={() => dispatch(visual.setMenuVisibility("leftmenu", true))}>
              <FiMenu />
            </Button>
          )}
        </div>

        {type !== "scrum-view" && (
          <DateButtons type={type} isDayView={isDayView} activeDate={activeDate} mondayDate={mondayDate} memberId={memberId} />
        )}
        {type === "scrum-view" && <div className="ScheduleButtons-filler"> </div>}

        <div className="center-buttons">
          {isInPlanningMode || (type === "team-plan" && !isMember) ? <PlanningModeButtons /> : null}

          {showLibrarian && !isViewOnly && (
            <Button variant="primary" onClick={handleLibrarianNavigation}>
            {t("qcLibrarian")}
          </Button>
          )}
          <Button variant="primary" onClick={() => navigate(isMember && directToMemberPlan ? `/member/${memberId}` : '/')}>
            <FiHome title={t("home")} />
          </Button>
          {/* The type condition is to hide the button if the settings-view is already shown */}
          {!isMember && !isViewOnly && !isOnMobile &&
            <Button variant="primary"  onClick={() => navigate("/setup/members")}>
              <FiSettings title={t("settings")} />
            </Button>
          }

          <DropdownButton
            title={
              (() => {
                const firstSegment = location.pathname.split('/')[1];
                const matchingOption = dropDownOptions.find(option => option.url.includes(`/${firstSegment}`));
                return matchingOption
                  ? matchingOption.name
                  : isCreatingProject
                    ? t("creatingProject")
                    : isEditingProject
                      ? t("editingProject")
                      : t("settings");
              })()
            }
            onSelect={(view) => navigate(view)}
          >
            {dropDownOptions.map((view) => {
              return (
                <Dropdown.Item key={view.url} active={view === location.pathname} eventKey={view.url}>
                  {view.name}
                </Dropdown.Item>
              )
            })}
          </DropdownButton>

          <UpdateIndicator/>

        </div>

        <div className="member-planningmode-week-buttons">
          {(type === "asset-view" || type === "team-plan") && (
            <Button
              title={showCompact ? t("viewNormal") : t("viewCompact")}
              onClick={() => {
                if (screenKey) {
                  dispatch(visual.setScreenShowCompact(screenKey, !showCompact))
                } else {
                  dispatch(visual.setShowCompact(!showCompact))
                }
              }}
            >
              {showCompact ? <TbArrowsMaximize/> : <TbArrowsMinimize/>}
            </Button>
          )}

          {type !== "sample-view" && type !== "project-view" && view !== "day-view" && type !== "scrum-view" && type !== "settings-view" && (
            <Button
              title={withWeek ? t("hideNextWeek") : t("showNextWeek")}
              onClick={() => {
                if (screenKey) {
                  dispatch(visual.setScreenExtraWeekVisibility(screenKey, !withWeek))
                } else {
                  dispatch(visual.setExtraWeekVisibility(!withWeek))
                }
              }}
            >
              {withWeek ? t("oneWeek") : t("twoWeeks")}
            </Button>
          )}

          {type !== "asset-view" &&
            type !== "sample-view" &&
            type !== "project-view" &&
            view !== "day-view" &&
            type !== "scrum-view" &&
            type !== "settings-view" && (
              <Button
                title={withWeekend ? t("hideSatSun") : t("showSatSun")}
                onClick={() => {
                  if (screenKey) {
                    dispatch(visual.setScreenWeekendVisibility(screenKey, !withWeekend))
                  } else {
                    dispatch(visual.setWeekendVisibility(!withWeekend))
                  }
                }}
              >
                {withWeekend ? t("hideSatSun") : t("showSatSun")}
              </Button>
            )}
          {type === "asset-view" && (
            <Button
            title={showWeekendAssetView ? t("hideSatSun") : t("showSatSun")}
            onClick={() => dispatch(visual.setShowWeekendAssetview(!showWeekendAssetView))}
          >
            {showWeekendAssetView ? t("hideSatSun") : t("showSatSun")}
          </Button>
          )}

          {member && (
            <Button title={t("configureMember")} id="config-member" onClick={() => setActiveConfig(true)}>
              <RiUserSettingsLine />
            </Button>
          )}

          {((showProjects && !showAnalyses) || (!showProjects && showAnalyses)) && (<Button
            variant="primary"
            onClick={() => {
              // Open help in new tab
              if (showAnalyses && !showProjects) {
                window.open("https://arnvind.com/plannertech-analysis-help/?pass=3af5b6917f3b195544496aa42b1159ea4396d9e24403f4f095d1a85c246033a2")
              } else if (showProjects && !showAnalyses) {
                window.open("https://arnvind.com/plannertech-project-help/?pass=4839c1c7cb609072a61d862d9111b54d7ec8831daea053069c2d279ad27c9a32")
              } else if(showProjects && showAnalyses) {

              }
            }}
            title={t("help")}
          >
            ?
          </Button>)}

          {showProjects && showAnalyses && (
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-basic" title={t("help")}>
                ?
              </Dropdown.Toggle>
              <Dropdown.Menu className="Schedule-help-dropdown">
                <Dropdown.Item
                  target="_blank"
                  href="https://arnvind.com/plannertech-analysis-help/?pass=3af5b6917f3b195544496aa42b1159ea4396d9e24403f4f095d1a85c246033a2"
                >
                  {t("analysis")}
                </Dropdown.Item>
                <Dropdown.Item
                  target="_blank"
                  href="https://arnvind.com/plannertech-project-help/?pass=4839c1c7cb609072a61d862d9111b54d7ec8831daea053069c2d279ad27c9a32"
                >
                  {t("project")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
          <Button
            variant="primary"
              onClick={() => {
                if (isInPlanningMode && isCurrentPlanner) {
                  dispatch(teamStatus.showLogoutConfirm(true))
                } else {
                  dispatch(auth.logout()).then(() => navigate("/login"))
                }
              }} title={t("logout")}
          >
            <FiLogOut />
          </Button>
        </div>

        <div className="rightmenu-button">
          {(type === "team-plan" || type === "single-member") && !isViewOnly && (!isMember || allowTaskTransfer) && (
            <Button title={t("openTodoList")} onClick={() => dispatch(visual.setMenuVisibility("rightmenu", true))}>
              <FiMenu />
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
